/**
 * F-137 — a date field that looks like a DECISION, not a form control.
 *
 * `<input type="date">` renders "dd/mm/yyyy" and a calendar glyph the moment
 * it exists: three placeholder segments that read as a half-filled form, in a
 * separator order that is wrong for half the people who see it, next to an
 * icon that is the only part of the control that actually opens the picker.
 * This is one button instead — "Set script due date" before, the date itself
 * after — and the picker it opens is the browser's own, anchored to the
 * button, so nothing is lost but the placeholder.
 *
 * How: a real `<input type="date">` still holds the value and paints the
 * picker (a hand-built calendar would be a worse one, and would not speak the
 * viewer's locale, keyboard or screen reader), but it is transparent and laid
 * over the button so `showPicker()` anchors where the user clicked.
 * `showPicker` needs a user gesture and is not everywhere; when it refuses,
 * the input un-hides and behaves exactly as it did before this component —
 * a visible fallback beats a button that silently does nothing.
 */
import { useRef, useState } from "react";

import { formatDateKey } from "./date-key.js";

export interface DateButtonProps {
  /** `YYYY-MM-DD`, or "" when nothing is set yet. */
  value: string;
  onChange: (next: string) => void;
  /** What the button says while nothing is set — "Set script due date". */
  placeholder: string;
  /**
   * What the date belongs to, when the button's own words are not enough to
   * tell it from the next one — a board name, on a wall of cards. Appended to
   * the accessible name, never shown: the card already says it in print.
   */
  context?: string;
  /**
   * Words in front of the date once one is set — "Script due Sep 30". Not part
   * of the placeholder, which already says what the date is for.
   */
  prefix?: string;
  /** Earliest selectable day, `YYYY-MM-DD`. */
  min?: string;
  disabled?: boolean;
  id?: string;
  /** Extra classes on the button — callers style the chip form of this. */
  className?: string;
}

export function DateButton({
  value,
  onChange,
  placeholder,
  context,
  prefix,
  min,
  disabled = false,
  id,
  className = "",
}: DateButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Set once `showPicker()` has refused: from then on this instance is the
  // plain input. Never reset — a browser that cannot open a picker on demand
  // will not learn to mid-session.
  const [fallback, setFallback] = useState(false);

  const open = () => {
    const input = inputRef.current;
    if (input === null || disabled) return;
    try {
      input.showPicker();
    } catch {
      setFallback(true);
      // The state change has not rendered yet, so focus after it has.
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const text =
    value === ""
      ? placeholder
      : prefix === undefined
        ? formatDateKey(value)
        : `${prefix} ${formatDateKey(value)}`;
  const name = context === undefined ? text : `${text} — ${context}`;

  return (
    <div className={`date-button-wrap${fallback ? " date-button-fallback" : ""}`}>
      {!fallback && (
        <button
          type="button"
          id={id}
          className={`date-button${value === "" ? " date-button-empty" : ""} ${className}`.trim()}
          onClick={open}
          disabled={disabled}
          // The accessible name STARTS with what the button says, so the two
          // agree for anyone using both (WCAG's label-in-name); the context
          // and the "change it" only add what the eye gets from the layout.
          aria-label={value === "" ? name : `${name}. Change it.`}
        >
          {text}
        </button>
      )}
      <input
        ref={inputRef}
        // `aria-hidden` while it is the invisible half of the button: the
        // button already carries the name and the value, and a screen reader
        // that found both would announce the field twice.
        {...(fallback ? { id, "aria-label": name } : { "aria-hidden": true, tabIndex: -1 })}
        className="date-button-input"
        type="date"
        value={value}
        min={min}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
